import type { CompanyRef } from "@wms/domain";
import { create } from "zustand";
import { persist } from "zustand/middleware";

export interface Me {
  user: { id: string; username: string; full_name: string; is_superuser: boolean };
  permissions: string[];
  feature_options: Record<string, boolean>;
  start_page?: string | null;
  preferences?: Record<string, unknown>;
  password_expired?: boolean;
  warehouse_ids?: string[];
  /** UX3010-13: our organizations the user may work in and the working one (ADR 0018). */
  companies?: CompanyRef[];
  company_id?: string | null;
}

interface AuthState {
  accessToken: string | null;
  refreshToken: string | null;
  me: Me | null;
  login: (username: string, password: string) => Promise<void>;
  loadMe: () => Promise<void>;
  /** true — new tokens; false — the session is over (401/403); throws when the API is unreachable. */
  refresh: () => Promise<boolean>;
  logout: (reason?: string) => void;
  /** Why the last session ended; shown on the login page (WMS-07). */
  sessionNotice: string | null;
  clearSessionNotice: () => void;
  can: (permission: string) => boolean;
  option: (code: string) => boolean;
  /** Merge a patch into the server-side ARM preferences (columns, theme, report variants). */
  savePreferences: (patch: Record<string, unknown>) => Promise<void>;
  pref: <T>(key: string) => T | undefined;
  /** UX3010-13: the working organization of this browser session; sent as `X-Company-Id` with every request. */
  company: CompanyRef | null;
  companies: () => CompanyRef[];
  /** Switches the working organization (the caller has already settled unsaved changes). */
  setCompany: (company: CompanyRef | null) => void;
}

const BASE = import.meta.env.VITE_API_URL ?? "/api/v1";

/**
 * UX3010-13: the working organization after the profile arrives — the one of this browser while the user may still
 * work in it (its name refreshed), else the one the server names (saved choice → default), else none.
 */
export function pickCompany(me: Me, current: CompanyRef | null): CompanyRef | null {
  const companies = me.companies ?? [];
  const kept = current ? companies.find((c) => c.id === current.id) : undefined;
  if (kept) return kept;
  return companies.find((c) => c.id === me.company_id) ?? null;
}

function authHeaders(token: string | null, company: CompanyRef | null): Record<string, string> {
  const headers: Record<string, string> = { Authorization: `Bearer ${token}` };
  if (company) headers["X-Company-Id"] = company.id;
  return headers;
}

export const useAuthStore = create<AuthState>()(
  persist(
    (set, get) => ({
      accessToken: null,
      refreshToken: null,
      me: null,
      company: null,
      sessionNotice: null,
      clearSessionNotice: () => set({ sessionNotice: null }),
      async login(username, password) {
        const body = new URLSearchParams({ username, password });
        const response = await fetch(`${BASE}/auth/login`, {
          method: "POST",
          headers: { "Content-Type": "application/x-www-form-urlencoded" },
          body,
        });
        if (!response.ok) {
          const problem = await response.json().catch(() => ({ title: "Ошибка входа" }));
          throw new Error(problem.detail ?? problem.title ?? "Ошибка входа");
        }
        const tokens = await response.json();
        set({ accessToken: tokens.access_token, refreshToken: tokens.refresh_token, sessionNotice: null });
        await get().loadMe();
      },
      async loadMe() {
        // the rights in the answer are those of the working organization: ask in the one this browser works in
        const company = get().company;
        let response = await fetch(`${BASE}/auth/me`, { headers: authHeaders(get().accessToken, company) });
        if (!response.ok && company && (response.status === 403 || response.status === 422)) {
          // the saved organization was closed or removed for this user: the server names the working one itself
          set({ company: null });
          response = await fetch(`${BASE}/auth/me`, { headers: authHeaders(get().accessToken, null) });
        }
        if (!response.ok) throw new Error("Не удалось загрузить профиль");
        const me = (await response.json()) as Me;
        set({ me, company: pickCompany(me, get().company) });
      },
      async refresh() {
        // another tab may have refreshed already: adopt its tokens instead of spending a used refresh token
        const before = get().accessToken;
        await useAuthStore.persist?.rehydrate?.();
        if (get().accessToken && get().accessToken !== before) return true;
        const token = get().refreshToken;
        if (!token) return false;
        let response: Response;
        try {
          response = await fetch(`${BASE}/auth/refresh`, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ refresh_token: token }),
          });
        } catch {
          throw new Error("Нет связи с сервером: сеанс сохранён, повторите действие позже");
        }
        if (response.status === 401 || response.status === 403) return false;
        if (!response.ok) throw new Error(`Сервер временно недоступен (${response.status}): сеанс сохранён`);
        const tokens = await response.json();
        set({ accessToken: tokens.access_token, refreshToken: tokens.refresh_token });
        return true;
      },
      logout(reason) {
        const { accessToken, refreshToken } = get();
        if (accessToken && refreshToken) {
          fetch(`${BASE}/auth/logout`, {
            method: "POST",
            headers: { "Content-Type": "application/json", Authorization: `Bearer ${accessToken}` },
            body: JSON.stringify({ refresh_token: refreshToken }),
          }).catch(() => undefined);
        }
        // the next sign-in (maybe of another user) starts from the organization saved on the server
        set({ accessToken: null, refreshToken: null, me: null, company: null, sessionNotice: reason ?? null });
      },
      async savePreferences(patch) {
        const response = await fetch(`${BASE}/auth/me/preferences`, {
          method: "PUT",
          headers: { "Content-Type": "application/json", ...authHeaders(get().accessToken, get().company) },
          body: JSON.stringify({ preferences: patch }),
        });
        if (response.ok) {
          const me = (await response.json()) as Me;
          set({ me, company: pickCompany(me, get().company) });
        }
      },
      pref<T>(key: string) {
        return get().me?.preferences?.[key] as T | undefined;
      },
      can(permission) {
        const me = get().me;
        if (!me) return false;
        if (me.user.is_superuser || me.permissions.includes("*") || me.permissions.includes(permission)) return true;
        const parts = permission.split(".");
        for (let i = parts.length - 1; i > 0; i--) {
          if (me.permissions.includes(parts.slice(0, i).join(".") + ".*")) return true;
        }
        return false;
      },
      option(code) {
        return get().me?.feature_options[code] ?? false;
      },
      companies() {
        return get().me?.companies ?? [];
      },
      setCompany(company) {
        set({ company });
      },
    }),
    { name: "wms-auth" },
  ),
);

/**
 * R07: several tabs share one session. When one tab refreshes the tokens (the refresh token is single-use) or someone
 * signs in as another user, the other tabs pick it up at once instead of later failing with a spent token and
 * dropping the user to the login page in the middle of work.
 */
if (typeof window !== "undefined") {
  window.addEventListener("storage", (event) => {
    if (event.key !== "wms-auth") return;
    const before = useAuthStore.getState().me?.user.id ?? null;
    void Promise.resolve(useAuthStore.persist?.rehydrate?.()).then(() => {
      const after = useAuthStore.getState().me?.user.id ?? null;
      // another user in this browser: reload so every screen shows who is really signed in
      if (before && after && before !== after) window.location.reload();
    });
  });
}
